import React, { useState, useEffect, useRef } from 'react';
import './LoginScreen.css';
import Logo from '../../assets/images/tektap.svg';

const LoginScreen = ({ onLoginSuccess }) => {
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const otpRefs = useRef([]);

  // Timer for resend OTP
  useEffect(() => {
    let interval;
    if (step === 'otp' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, resendTimer]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSendOTP = async () => {
    setError('');
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch('https://motobot.in/ttessapi/v1//user/auth/otp/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailAddress: email,
          phoneNumber: '',
          option: 'email'
        })
      });

      const data = await response.json();

      if (data.success) {
        setStep('otp');
        setResendTimer(30);
        setCanResend(false);
      } else {
        setError(data.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return;
    
    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);
    
    // Focus the next empty input or last input
    const nextEmptyIndex = newOtp.findIndex(val => !val);
    if (nextEmptyIndex !== -1) {
      otpRefs.current[nextEmptyIndex]?.focus();
    } else {
      otpRefs.current[3]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpValue = otp.join('');
    
    if (otpValue.length !== 4) {
      setError('Please enter complete OTP');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch('https://motobot.in/ttessapi/v1/user/auth/otp/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailAddress: email,
          phoneNumber: '',
          option: 'email',
          otpCode: otpValue
        })
      });

      const data = await response.json();

      if (data.success) {
        // Store token and user info in localStorage
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('userId', data.data.userId);
        if (data.data.userName) {
          localStorage.setItem('userName', data.data.userName);
        }
        onLoginSuccess();
      } else {
        setError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;
    
    setOtp(['', '', '', '']);
    setError('');
    setIsLoading(true);
    
    try {
      const response = await fetch('https://motobot.in/ttessapi/v1//user/auth/otp/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailAddress: email,
          phoneNumber: '',
          option: 'email'
        })
      });

      const data = await response.json();

      if (data.success) {
        setResendTimer(30);
        setCanResend(false);
      } else {
        setError(data.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtp(['', '', '', '']);
    setError('');
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-logo">
          <img src={Logo} alt="TekTap HRMS" />
          <span className="logo-text">HRMS</span>
        </div>

        {step === 'email' ? (
          <div className="login-form">
            <h1 className="login-title">Welcome!</h1>
            
            <div className="form-group">
              <label htmlFor="email">Enter your mail id</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="yourmail@gmail.com"
                className={error ? 'input-error' : ''}
                onKeyPress={(e) => e.key === 'Enter' && handleSendOTP()}
              />
              {error && <span className="error-message">{error}</span>}
            </div>

            <button 
              className="btn-send-otp"
              onClick={handleSendOTP}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading-spinner"></span>
              ) : (
                'Send OTP'
              )}
            </button>
          </div>
        ) : (
          <div className="login-form otp-form">
            <h1 className="login-title">Verify your email</h1>
            <p className="otp-subtitle">
              OTP sent to email - <span className="email-highlight">{email}</span>
            </p>

            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={index === 0 ? handleOtpPaste : undefined}
                  className={`otp-input ${error ? 'input-error' : ''}`}
                  autoFocus={index === 0}
                />
              ))}
            </div>
            
            {error && <span className="error-message center">{error}</span>}

            <button 
              className="btn-verify-otp"
              onClick={handleVerifyOTP}
              disabled={isLoading || otp.join('').length !== 4}
            >
              {isLoading ? (
                <span className="loading-spinner"></span>
              ) : (
                'Verify OTP'
              )}
            </button>

            <div className="resend-section">
              <button 
                className={`btn-resend ${canResend ? 'active' : ''}`}
                onClick={handleResendOTP}
                disabled={!canResend}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 4v6h-6" />
                  <path d="M1 20v-6h6" />
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                </svg>
                Resend OTP {!canResend && `in ${resendTimer}s`}
              </button>
            </div>

            <button className="btn-back" onClick={handleBackToEmail}>
              ← Change email
            </button>
          </div>
        )}

        <div className="login-dots">
          <span className={`dot ${step === 'email' ? 'active' : ''}`}></span>
          <span className={`dot ${step === 'otp' ? 'active' : ''}`}></span>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;

